import { logError, logInfo } from '../lib/logger';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001';

/**
 * 後端統一的錯誤格式（見 apps/api 的 ApiExceptionFilter）。
 * 前端一律看 `code` 做判斷，`message` 只拿來顯示——訊息會隨文案調整。
 */
export interface ApiErrorBody {
  statusCode: number;
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

/**
 * http 層跟 auth store 之間的橋。
 *
 * 直接 import store 會變成循環相依（store 要打 API、API 要拿 token），所以反過來
 * 由 store 在啟動時把自己註冊進來。
 */
export interface AuthBridge {
  getAccessToken: () => string | null;
  /** 401 時呼叫；成功回新的 access token，失敗回 null（代表得重新登入）。 */
  refresh: () => Promise<string | null>;
  /** refresh 也失敗了：清狀態、導回登入頁。 */
  onAuthLost: () => void;
  /** 403：權限不足。通常是 UI 沒藏好，或角色被降級了。 */
  onForbidden: (error: ApiError) => void;
}

const NOOP_BRIDGE: AuthBridge = {
  getAccessToken: () => null,
  refresh: () => Promise.resolve(null),
  onAuthLost: () => undefined,
  onForbidden: () => undefined,
};

let bridge: AuthBridge = NOOP_BRIDGE;

export function setAuthBridge(next: AuthBridge): void {
  bridge = next;
}

export function resetAuthBridge(): void {
  bridge = NOOP_BRIDGE;
}

/**
 * 同時有多個請求撞到 401 時，只跑一次 refresh，其他人等同一個 promise。
 * 不這樣做的話，五個並行請求會發五次 refresh，而 rotation 會讓後四次全部失敗，
 * 反而把使用者踢出去。
 */
let inFlightRefresh: Promise<string | null> | null = null;

function refreshOnce(): Promise<string | null> {
  inFlightRefresh ??= bridge.refresh().finally(() => {
    inFlightRefresh = null;
  });

  return inFlightRefresh;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** 內部用：避免 refresh 之後無限重試。 */
  retry?: boolean;
  signal?: AbortSignal;
  /**
   * 「這幾個狀態碼是預期內的答案」——記 info 不記 error。
   *
   * 例：訪客打 /auth/refresh 一定 400（身上沒有 cookie），那是「還沒登入」不是壞掉。
   * 每個 4xx 都印紅字的話，真正的錯誤反而被蓋掉。
   */
  expectedStatuses?: number[];
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) {
    return null;
  }

  return response.json().catch(() => null);
}

function toApiError(status: number, body: unknown): ApiError {
  const record = (body ?? {}) as Partial<ApiErrorBody>;

  return new ApiError(
    status,
    typeof record.code === 'string' ? record.code : 'unknown_error',
    typeof record.message === 'string' ? record.message : `請求失敗（${status}）`,
    record.details,
  );
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, retry = false, signal, expectedStatuses = [] } = options;
  const token = bridge.getAccessToken();

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      accept: 'application/json',
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(token === null ? {} : { authorization: `Bearer ${token}` }),
    },
    // refresh token 在 httpOnly cookie 裡，跨 origin 要靠這個才會帶上
    credentials: 'include',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(signal === undefined ? {} : { signal }),
  });

  if (response.ok) {
    return (await parseBody(response)) as T;
  }

  const error = toApiError(response.status, await parseBody(response));

  // access token 過期：換一張再試一次。/auth/* 自己不重試，不然 refresh 失敗會遞迴
  if (error.isUnauthorized && !retry && !path.startsWith('/auth/')) {
    const refreshed = await refreshOnce();

    if (refreshed !== null) {
      return apiRequest<T>(path, { ...options, retry: true });
    }

    bridge.onAuthLost();
  }

  if (error.isForbidden) {
    bridge.onForbidden(error);
  }

  const context = { path, method, status: error.status, code: error.code };

  if (expectedStatuses.includes(error.status)) {
    logInfo('API 請求得到預期內的失敗', context);
  } else {
    logError('API 請求失敗', error, context);
  }

  throw error;
}

export const apiGet = <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
  apiRequest<T>(path, { ...options, method: 'GET' });

export const apiPost = <T>(path: string, body?: unknown) =>
  apiRequest<T>(path, { method: 'POST', body });

export const apiPatch = <T>(path: string, body?: unknown) =>
  apiRequest<T>(path, { method: 'PATCH', body });

export const apiDelete = <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' });
